'use client';
import {
  IconChevronLeft,
  IconChevronRight,
  IconSizingExpand,
  IconX,
} from '@veneer/core';
import {
  createHandleThumbnailClick,
  getDefaultIndex,
  goToNextSlide,
  goToPrevSlide,
  handleWrapperScroll,
  handleTracerClick,
  keyboardEvent,
  Slides,
} from './carousel-utils';
import slidesInformation from './slides.json';
import { buildPath } from '@storefront/utils/utils';
import styles from './index.module.scss';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

export const Carousel = () => {
  const slides: Slides[] = slidesInformation;
  const defaultIndex = getDefaultIndex(slides);

  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const [isFullscreenDisplayed, setIsFullscreenDisplayed] = useState(false);
  const thumbnailWrapperRef = useRef<HTMLDivElement | null>(null);
  const thumbnailContainerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: { key: string }) => {
      if (e.key === 'Escape' && isFullscreenDisplayed) {
        setIsFullscreenDisplayed(false);
      }
    };

    if (isFullscreenDisplayed) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreenDisplayed]);

  const handleGoToNextSlide = () => {
    setCurrentIndex((prevIndex: number) =>
      goToNextSlide(prevIndex, slides.length)
    );
  };

  const handleGoToPrevSlide = () => {
    setCurrentIndex((prevIndex: number) =>
      goToPrevSlide(prevIndex, slides.length)
    );
  };

  const handleThumbnailClick = createHandleThumbnailClick(setCurrentIndex);

  const onTracerClick = (index: number) =>
    handleTracerClick(index, thumbnailContainerRefs.current);

  const onWrapperScroll = () =>
    handleWrapperScroll(
      thumbnailWrapperRef.current,
      thumbnailContainerRefs.current,
      setCurrentIndex
    );

  return (
    <>
      {isFullscreenDisplayed && (
        <div className={styles['fullscreen-image']}>
          <IconChevronLeft
            className={styles['expanded-chevron-left']}
            data-testid="chevron-left"
            onClick={handleGoToPrevSlide}
          />
          <IconX
            className={styles['close-expand']}
            data-testid="close-expand"
            onClick={() => setIsFullscreenDisplayed(!isFullscreenDisplayed)}
            tabIndex={0}
            onKeyDown={(event) =>
              keyboardEvent(event, () =>
                setIsFullscreenDisplayed(!isFullscreenDisplayed)
              )
            }
          />
          <img
            src={buildPath(slides[currentIndex].url)}
            alt={`Slide ${slides[currentIndex].id}`}
            style={{ backgroundColor: '#f0f0f0' }}
          />
          <IconChevronRight
            className={styles['expanded-chevron-right']}
            data-testid="chevron-right"
            onClick={handleGoToNextSlide}
          />
        </div>
      )}
      <div className={styles['container']}>
        <div className={styles['content-thumbnail']}>
          <div
            className={styles['wrapper-thumbnail']}
            data-testid="thumbnail-wrapper"
            ref={thumbnailWrapperRef}
            onScroll={onWrapperScroll}
          >
            <div className={styles['thumbnail']}>
              {slides.map((slide, index) => (
                <div
                  className={classNames(styles['thumbnail-image-container'], {
                    [styles['thumbnail-image-container-active']]:
                      currentIndex === slide.id,
                  })}
                  data-testid={`thumbnail-image-container-${slide.id}`}
                  key={slide.id}
                  ref={(el) => {
                    thumbnailContainerRefs.current[index] = el;
                  }}
                >
                  <button
                    type="button"
                    onClick={handleThumbnailClick(index)}
                    onKeyDown={(event) =>
                      keyboardEvent(event, () => handleThumbnailClick(index))
                    }
                    tabIndex={0}
                    className={styles['thumbnail-button']}
                    aria-label={`View slide ${slide.id}`}
                  >
                    <img
                      key={slide.content}
                      src={buildPath(slide.url)}
                      alt={`Thumbnail ${slide.id}`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className={styles['tracer']}>
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                data-testid={`Tracer ${slide.id}`}
                onClick={() => onTracerClick(index)}
                className={styles['tracer-btn']}
              >
                <div
                  className={
                    index === currentIndex ? styles['dash'] : styles['dot']
                  }
                  style={{
                    backgroundImage: `url(${buildPath(
                      index === currentIndex ? 'dash.png' : 'dot.png'
                    )})`,
                  }}
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
        <div className={styles['content-image']}>
          <IconChevronLeft
            className={styles['chevron-left']}
            data-testid="chevron-left"
            onClick={handleGoToPrevSlide}
          />
          <IconSizingExpand
            className={styles['expand-icon']}
            data-testid="sizing-expand"
            onClick={() => setIsFullscreenDisplayed(!isFullscreenDisplayed)}
            tabIndex={0}
            onKeyDown={(e) =>
              keyboardEvent(e, () =>
                setIsFullscreenDisplayed(!isFullscreenDisplayed)
              )
            }
          />
          <div className={styles['image']}>
            <img
              src={buildPath(slides[currentIndex].url)}
              alt={`Slide ${slides[currentIndex].id}`}
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </div>
          <IconChevronRight
            className={styles['chevron-right']}
            data-testid="chevron-right"
            onClick={handleGoToNextSlide}
          />
        </div>
      </div>
    </>
  );
};
